import { HighlightStyle, syntaxHighlighting } from "@codemirror/language";
import type { Extension } from "@codemirror/state";
import { EditorView } from "@codemirror/view";
import { tags } from "@lezer/highlight";

/*
 * Everything here reads `--z-*` custom properties from @z/tokens, so the editor follows the
 * light/dark theme (and `data-theme` overrides) without a CodeMirror dark theme of its own.
 */

const fg = "var(--z-color-text)";
const muted = "var(--z-color-text-muted)";
const faint = "var(--z-color-text-faint)";
const accent = "var(--z-color-accent)";
const mono = "var(--z-font-mono)";
const semibold = "var(--z-font-weight-semibold)";

export const editorTheme: Extension = EditorView.theme({
  "&": {
    height: "100%",
    backgroundColor: "transparent",
    color: fg,
    fontFamily: "var(--z-font-sans)",
    fontSize: "var(--z-font-size-base)",
  },
  "&.cm-focused": { outline: "none" },
  ".cm-scroller": {
    overflow: "auto",
    fontFamily: "inherit",
    lineHeight: "1.65",
  },
  ".cm-content": {
    boxSizing: "border-box",
    width: "100%",
    maxWidth: "72ch",
    margin: "0 auto",
    padding: "var(--z-space-6) var(--z-space-6) var(--z-space-16)",
    caretColor: fg,
  },
  ".cm-line": { padding: "0" },
  ".cm-cursor, .cm-dropCursor": { borderLeftColor: fg },
  "&.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground, .cm-selectionBackground":
    { backgroundColor: "var(--z-color-selection)" },
  ".cm-content ::selection": { backgroundColor: "var(--z-color-selection)" },
  ".cm-placeholder": { color: faint, fontStyle: "normal" },
  ".cm-matchingBracket, &.cm-focused .cm-matchingBracket": {
    backgroundColor: "var(--z-color-accent-muted)",
    outline: "none",
  },
  ".cm-nonmatchingBracket, &.cm-focused .cm-nonmatchingBracket": {
    color: "var(--z-color-danger)",
  },
});

export const markdownHighlightStyle = HighlightStyle.define([
  { tag: tags.heading1, fontWeight: semibold, fontSize: "1.6em" },
  { tag: tags.heading2, fontWeight: semibold, fontSize: "1.35em" },
  { tag: tags.heading3, fontWeight: semibold, fontSize: "1.15em" },
  { tag: tags.heading, fontWeight: semibold },
  { tag: tags.strong, fontWeight: semibold },
  { tag: tags.emphasis, fontStyle: "italic" },
  { tag: tags.strikethrough, textDecoration: "line-through", color: muted },
  { tag: tags.monospace, fontFamily: mono, fontSize: "0.92em" },
  { tag: tags.link, color: accent, textDecoration: "underline" },
  { tag: tags.url, color: accent },
  { tag: tags.quote, color: muted, fontStyle: "italic" },
  { tag: tags.list, color: fg },
  { tag: tags.atom, color: accent },
  { tag: tags.contentSeparator, color: faint },
  { tag: tags.processingInstruction, color: faint },
  { tag: tags.labelName, color: accent },
  { tag: tags.escape, color: muted },
  // Fenced code blocks in other languages (via @codemirror/language-data).
  { tag: tags.comment, color: faint, fontStyle: "italic" },
  { tag: [tags.keyword, tags.modifier, tags.operatorKeyword], color: accent },
  { tag: [tags.string, tags.special(tags.string)], color: "var(--z-color-success)" },
  { tag: [tags.number, tags.bool, tags.null], color: "var(--z-color-warning)" },
  { tag: [tags.typeName, tags.className], color: fg, fontWeight: semibold },
  { tag: [tags.function(tags.variableName), tags.function(tags.propertyName)], color: fg },
  { tag: tags.invalid, color: "var(--z-color-danger)" },
]);

export const editorHighlighting: Extension = syntaxHighlighting(markdownHighlightStyle);
